import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { cereriAPI } from '../services/api';
import { Card, StatusBadge, PrioritateBadge, Spinner, Button, PageHeader } from '../components/common';
import { formatDataOra, formatBytes, STATUS_LABELS, TIP_LABELS } from '../utils';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import { useWindowWidth } from '../hooks/useWindowWidth';

const STATUSURI_TRANZITIE = {
  inregistrata: ['in_analiza', 'respinsa'],
  in_analiza:   ['in_lucru',   'respinsa'],
  in_lucru:     ['rezolvata',  'respinsa'],
  rezolvata:    ['arhivata'],
  respinsa:     ['arhivata'],
  arhivata:     [],
};

export default function CerereDetaliuPage() {
  const { id } = useParams();
  const { isFunctionar } = useAuth();
  const w = useWindowWidth();
  const isMobile = w < 768;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showStatusForm, setShowStatusForm] = useState(false);
  const [statusNou,   setStatusNou]   = useState('');
  const [comentariu,  setCometariu]   = useState('');
  const [raspuns,     setRaspuns]     = useState('');
  const [intern,      setIntern]      = useState(false);

  const { data, isLoading } = useQuery(
    ['cerere', id],
    () => cereriAPI.get(id).then(r => r.data.data),
    { enabled: !!id }
  );

  const mutation = useMutation(
    (payload) => cereriAPI.updateStatus(id, payload),
    {
      onSuccess: () => {
        toast.success('Status actualizat cu succes');
        queryClient.invalidateQueries(['cerere', id]);
        queryClient.invalidateQueries('cereri');
        setShowStatusForm(false);
        setStatusNou(''); setCometariu(''); setRaspuns(''); setIntern(false);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Eroare actualizare'),
    }
  );

  if (isLoading) return <Layout><Spinner message="Se încarcă cererea..." /></Layout>;
  if (!data)     return <Layout><p>Cerere negăsită.</p></Layout>;

  const c = data;
  const tranzitii = STATUSURI_TRANZITIE[c.status] || [];

  return (
    <Layout>
      <div style={s.backRow}>
        <button onClick={() => navigate('/cereri')} style={s.back}>← Înapoi la cereri</button>
      </div>

      {/* Header cerere */}
      <div style={s.header}>
        <div>
          <div style={s.numar}>{c.numar_cerere}</div>
          <h1 style={s.titlu}>{c.titlu}</h1>
          <div style={s.metaRow}>
            <StatusBadge status={c.status} />
            <PrioritateBadge prioritate={c.prioritate} />
            <span style={s.metaTag}>{TIP_LABELS[c.tip] || c.tip}</span>
            <span style={s.metaTag}>{c.categorie}</span>
          </div>
        </div>
        {isFunctionar && (
          <Link to={`/cereri/${id}/flux`} style={{ textDecoration: 'none' }}>
            <Button variant="secondary" style={{ marginRight: '8px' }}>🔄 Flux document</Button>
          </Link>
        )}
        {isFunctionar && tranzitii.length > 0 && (
          <Button onClick={() => setShowStatusForm(!showStatusForm)}>
            {showStatusForm ? 'Anulează' : 'Actualizează status'}
          </Button>
        )}
      </div>

      {/* Formular actualizare status (funcționar) */}
      {showStatusForm && isFunctionar && (
        <Card title="Actualizare status">
          <div style={s.statusForm}>
            <div style={s.field}>
              <label style={s.label}>Status nou *</label>
              <select value={statusNou} onChange={e => setStatusNou(e.target.value)} style={s.select}>
                <option value="">Selectează...</option>
                {tranzitii.map(st => (
                  <option key={st} value={st}>{STATUS_LABELS[st] || st}</option>
                ))}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Răspuns public pentru cetățean</label>
              <textarea
                rows={3} value={raspuns} onChange={e => setRaspuns(e.target.value)}
                placeholder="Mesaj vizibil de cetățean..."
                style={s.textarea}
              />
            </div>
            <div style={s.field}>
              <label style={s.label}>Comentariu intern (invizibil pentru cetățean)</label>
              <textarea
                rows={2} value={comentariu} onChange={e => setCometariu(e.target.value)}
                placeholder="Note interne..."
                style={{ ...s.textarea, backgroundColor: '#fffbeb' }}
              />
            </div>
            <label style={s.checkRow}>
              <input type="checkbox" checked={intern} onChange={e => setIntern(e.target.checked)} />
              <span>Marchează ca note interne (nu notifică cetățeanul)</span>
            </label>
            <Button
              onClick={() => mutation.mutate({ status: statusNou, comentariu, raspunsPublic: raspuns, intern })}
              disabled={!statusNou || mutation.isLoading}
            >
              {mutation.isLoading ? 'Se salvează...' : 'Salvează actualizarea'}
            </Button>
          </div>
        </Card>
      )}

      <div style={{ ...s.grid2, gridTemplateColumns: isMobile ? '1fr' : '1fr 320px' }}>
        {/* Detalii cerere */}
        <div>
          <Card title="Descriere">
            <p style={s.descriere}>{c.descriere}</p>
            {c.locatie && (
              <div style={s.locatieRow}>
                <span style={s.metaKey}>📍 Locație:</span>
                <span>{c.locatie}</span>
              </div>
            )}
            {c.raspuns_public && (
              <div style={s.raspunsPublic}>
                <div style={s.raspunsLabel}>Răspuns primărie:</div>
                <p style={s.raspunsText}>{c.raspuns_public}</p>
              </div>
            )}
          </Card>

          {/* Documente oficiale (generate prin flux — adresă/certificat/răspuns/dispoziție) */}
          {data.documente?.length > 0 && (
            <Card title={`Documente oficiale (${data.documente.length})`}>
              {data.documente.map(d => (
                <a key={d.id} href={d.downloadUrl} target="_blank" rel="noopener noreferrer" style={s.fisierRow}>
                  <span style={s.fisierIcon}>📄</span>
                  <span style={s.fisierName}>{d.titlu}</span>
                  <span style={{ ...s.fisierSize, textTransform: 'capitalize' }}>{d.status}</span>
                  <span style={s.downloadIcon}>↓</span>
                </a>
              ))}
            </Card>
          )}

          {/* Atașamente */}
          {data.atasamente?.length > 0 && (
            <Card title={`Atașamente (${data.atasamente.length})`}>
              {data.atasamente.map(a => (
                <a key={a.id} href={a.downloadUrl} target="_blank" rel="noopener noreferrer" style={s.fisierRow}>
                  {a.sub_numar && (
                    <span style={{ fontSize: '10px', background: '#2d2d7e', color: '#fff', borderRadius: '4px', padding: '1px 6px', marginRight: '4px', flexShrink: 0 }}>
                      D{a.sub_numar}
                    </span>
                  )}
                  <span style={s.fisierIcon}>{getFileIcon(a.tip_mime)}</span>
                  <span style={s.fisierName}>{a.eticheta || a.nume_fisier}</span>
                  <span style={s.fisierSize}>{formatBytes(a.marime)}</span>
                  <span style={s.downloadIcon}>↓</span>
                </a>
              ))}
            </Card>
          )}
        </div>

        {/* Sidebar info + Istoric */}
        <div>
          <Card title="Informații">
            {[
              { key: 'Nr. intrare',   val: c.numar_intrare || '—' },
              ...(c.numar_iesire ? [{ key: 'Nr. ieșire', val: c.numar_iesire }] : []),
              { key: 'Solicitant',    val: c.solicitant_complet || c.solicitant_nume },
              { key: 'Email',         val: c.solicitant_email },
              { key: 'Depusă la',     val: formatDataOra(c.created_at) },
              ...(c.status === 'arhivata' || c.data_arhivare ? [
                { key: '📁 Data arhivare', val: formatDataOra(c.data_arhivare || c.updated_at) },
                { key: '🗄️ Nr. arhivă',     val: c.numar_arhiva || '—' }
              ] : []),
              { key: 'Actualizată',   val: formatDataOra(c.updated_at) },
              { key: 'Termen legal',  val: c.termen_legal ? formatDataOra(c.termen_legal) : '—' },
              { key: 'Sursă',         val: c.sursa },
              ...(isFunctionar ? [
                { key: 'Asignat la', val: c.asignat_complet || '—' },
                { key: 'Rezolvat de', val: c.rezolvat_la ? `${c.asignat_complet} la ${formatDataOra(c.rezolvat_la)}` : '—' },
              ] : []),
            ].map(row => (
              <div key={row.key} style={s.infoRow}>
                <span style={s.infoKey}>{row.key}:</span>
                <span style={s.infoVal}>{row.val || '—'}</span>
              </div>
            ))}
          </Card>

          <Card title="Istoric">
            {data.istoric?.map((h, i) => (
              <div key={i} style={{ ...s.istoricItem, ...(h.intern ? s.istoricIntern : {}) }}>
                <div style={s.istoricHeader}>
                  <StatusBadge status={h.status_nou} />
                  {h.intern && <span style={s.internTag}>intern</span>}
                </div>
                {h.comentariu && <p style={s.istoricText}>{h.comentariu}</p>}
                <div style={s.istoricMeta}>
                  <span>{h.modificat_de || 'Sistem'}</span>
                  <span>{formatDataOra(h.created_at)}</span>
                </div>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </Layout>
  );
}

function getFileIcon(mime) {
  if (mime?.includes('pdf'))   return '📄';
  if (mime?.includes('image')) return '🖼';
  if (mime?.includes('word'))  return '📝';
  return '📎';
}

const s = {
  backRow:      { marginBottom: '16px' },
  back:         { background: 'none', border: 'none', cursor: 'pointer', color: '#2d2d7e', fontSize: '14px', padding: 0 },
  header:       { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  numar:        { fontSize: '12px', fontFamily: 'monospace', color: '#888', marginBottom: '8px' },
  titlu:        { margin: '0 0 12px', fontSize: '20px', fontWeight: '700', color: '#1a1a2e' },
  metaRow:      { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' },
  metaTag:      { fontSize: '12px', backgroundColor: '#f0f0f0', color: '#555', padding: '3px 10px', borderRadius: '20px' },
  grid2:        { display: 'grid', gridTemplateColumns: '1fr 320px', gap: '16px', alignItems: 'start' },
  descriere:    { margin: 0, lineHeight: '1.7', color: '#333', whiteSpace: 'pre-wrap' },
  locatieRow:   { display: 'flex', gap: '8px', marginTop: '16px', fontSize: '14px', color: '#555' },
  metaKey:      { fontWeight: '600', flexShrink: 0 },
  raspunsPublic:{ marginTop: '20px', padding: '16px', backgroundColor: '#eaf3de', borderRadius: '8px', borderLeft: '4px solid #3b6d11' },
  raspunsLabel: { fontSize: '12px', fontWeight: '700', color: '#27500a', marginBottom: '8px' },
  raspunsText:  { margin: 0, color: '#333', lineHeight: '1.6' },
  fisierRow:    { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f5f5f5', textDecoration: 'none', color: '#1a1a2e' },
  fisierIcon:   { fontSize: '20px', flexShrink: 0 },
  fisierName:   { flex: 1, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fisierSize:   { fontSize: '12px', color: '#aaa', flexShrink: 0 },
  downloadIcon: { color: '#2d2d7e', fontWeight: '700', flexShrink: 0 },
  infoRow:      { display: 'flex', gap: '8px', padding: '8px 0', borderBottom: '1px solid #f5f5f5', fontSize: '14px' },
  infoKey:      { color: '#888', flexShrink: 0, minWidth: '90px' },
  infoVal:      { color: '#1a1a2e', fontWeight: '500', wordBreak: 'break-word' },
  istoricItem:  { padding: '12px 0', borderBottom: '1px solid #f5f5f5' },
  istoricIntern:{ backgroundColor: '#fffbeb', padding: '8px 12px', borderRadius: '6px', marginBottom: '4px' },
  istoricHeader:{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' },
  internTag:    { fontSize: '11px', backgroundColor: '#faeeda', color: '#633806', padding: '2px 8px', borderRadius: '10px' },
  istoricText:  { margin: '4px 0', fontSize: '13px', color: '#444', lineHeight: '1.5' },
  istoricMeta:  { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#aaa', marginTop: '4px' },
  statusForm:   { display: 'flex', flexDirection: 'column', gap: '16px' },
  field:        { display: 'flex', flexDirection: 'column', gap: '6px' },
  label:        { fontSize: '13px', fontWeight: '500', color: '#555' },
  select:       { padding: '9px 12px', fontSize: '14px', border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none' },
  textarea:     { padding: '10px', fontSize: '14px', border: '1.5px solid #ddd', borderRadius: '8px', outline: 'none', resize: 'vertical', fontFamily: 'inherit' },
  checkRow:     { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' },
};

